<template>
  <div class="app-wrapper">
    <!-- Navigation -->
    <nav class="navbar navbar-expand-lg navbar-dark">
      <div class="container">
        <router-link to="/" class="navbar-brand">
          <img src="/cardano-logo.svg" alt="Bank of Cardano" class="brand-logo" />
          <div class="brand-text">
            <span class="brand-name">Bank of Cardano</span>
            <span class="brand-tagline">Matrix Financial Instruments</span>
          </div>
        </router-link>

        <button
          class="navbar-toggler"
          type="button"
          @click="mobileMenuOpen = !mobileMenuOpen"
        >
          <span class="navbar-toggler-icon"></span>
        </button>

        <div class="collapse navbar-collapse" :class="{ show: mobileMenuOpen }" id="navbarNav">
          <ul class="navbar-nav mr-auto">
            <!-- Spacer to push right content -->
          </ul>

          <div class="nav-actions">
            <!-- Smart Contracts Dropdown -->
            <div
              v-if="showSmartContractsMenu"
              class="nav-dropdown-container"
              @mouseenter="handleSmartContractsMouseEnter"
              @mouseleave="handleSmartContractsMouseLeave"
            >
              <button
                class="smart-contracts-btn"
                @click.prevent="toggleSmartContractsDropdown"
                :class="{ 'show': showSmartContractsDropdown }"
              >
                <i class="fas fa-file-contract mr-1"></i>
                Smart Contracts
                <i class="fas fa-chevron-down ml-1 chevron-icon"></i>
              </button>
              <div
                class="dropdown-menu"
                :class="{ 'show': showSmartContractsDropdown }"
              >
                <router-link
                  v-if="siteControls.controls.enableCredentials"
                  to="/credentials"
                  class="dropdown-item"
                  @click="showSmartContractsDropdown = false; mobileMenuOpen = false"
                >
                  <i class="fas fa-id-badge mr-2"></i>Smart <span class="dropdown-accent-word">Credentials</span>
                </router-link>
                <router-link
                  v-if="siteControls.controls.enableAssets"
                  to="/assets"
                  class="dropdown-item"
                  @click="showSmartContractsDropdown = false; mobileMenuOpen = false"
                >
                  <i class="fas fa-gem mr-2"></i>Smart <span class="dropdown-accent-word">Assets</span>
                </router-link>
                <div
                  v-if="showSmartContractsDivider"
                  class="dropdown-divider dropdown-divider-gradient"
                  role="separator"
                ></div>
                <router-link
                  v-if="siteControls.controls.enableLoan"
                  to="/loan"
                  class="dropdown-item"
                  @click="showSmartContractsDropdown = false; mobileMenuOpen = false"
                >
                  <i class="fas fa-file-invoice-dollar mr-2"></i>Loans
                </router-link>
                <router-link
                  v-if="siteControls.controls.enableBonds"
                  to="/cdo"
                  class="dropdown-item"
                  @click="showSmartContractsDropdown = false; mobileMenuOpen = false"
                >
                  <i class="fas fa-layer-group mr-2"></i>Bonds
                </router-link>
                <router-link
                  v-if="siteControls.controls.enableTrade"
                  to="/trade"
                  class="dropdown-item"
                  @click="showSmartContractsDropdown = false; mobileMenuOpen = false"
                >
                  <i class="fas fa-exchange-alt mr-2"></i>Trade
                </router-link>
                <router-link
                  v-if="siteControls.controls.enableAuction"
                  to="/auction"
                  class="dropdown-item"
                  @click="showSmartContractsDropdown = false; mobileMenuOpen = false"
                >
                  <i class="fas fa-gavel mr-2"></i>Auctions
                </router-link>
                <router-link
                  v-if="siteControls.controls.enableSafe"
                  to="/safe"
                  class="dropdown-item"
                  @click="showSmartContractsDropdown = false; mobileMenuOpen = false"
                >
                  <i class="fas fa-rocket mr-2"></i>Fundraising
                </router-link>
                <router-link
                  v-if="siteControls.controls.enableFracture"
                  to="/fracture"
                  class="dropdown-item"
                  @click="showSmartContractsDropdown = false; mobileMenuOpen = false"
                >
                  <i class="fas fa-puzzle-piece mr-2"></i>Fractionalization
                </router-link>
                <router-link
                  v-if="siteControls.controls.enableCds"
                  to="/cds"
                  class="dropdown-item"
                  @click="showSmartContractsDropdown = false; mobileMenuOpen = false"
                >
                  <i class="fas fa-shield-alt mr-2"></i>Credit Swaps
                </router-link>
                <router-link
                  v-if="siteControls.controls.enableTrustFund"
                  to="/trust-fund"
                  class="dropdown-item"
                  @click="showSmartContractsDropdown = false; mobileMenuOpen = false"
                >
                  <i class="fas fa-university mr-2"></i>Trust Funds
                </router-link>
              </div>
            </div>

            <div
              v-if="siteControls.controls.enableSimulation"
              class="nav-dropdown-container"
              @mouseenter="handleSimulationMouseEnter"
              @mouseleave="handleSimulationMouseLeave"
            >
              <button
                class="simulation-center-btn"
                @click.prevent="toggleSimulationDropdown"
                :class="{ 'show': showSimulationDropdown }"
              >
                <i class="fas fa-flask mr-1"></i>
                Simulate
                <i class="fas fa-chevron-down ml-1 chevron-icon"></i>
              </button>
              <div
                class="dropdown-menu"
                :class="{ 'show': showSimulationDropdown }"
              >
                <router-link
                  to="/simulation/config"
                  class="dropdown-item"
                  @click="showSimulationDropdown = false; mobileMenuOpen = false"
                >
                  <i class="fas fa-sliders-h mr-2"></i>Configure
                </router-link>
                <router-link
                  to="/simulation"
                  class="dropdown-item"
                  @click="showSimulationDropdown = false; mobileMenuOpen = false"
                >
                  <i class="fas fa-play-circle mr-2"></i>Execute
                </router-link>
                <router-link
                  to="/analytics"
                  class="dropdown-item"
                  @click="showSimulationDropdown = false; mobileMenuOpen = false"
                >
                  <i class="fas fa-chart-line mr-2"></i>Analyze
                </router-link>
              </div>
            </div>
            <div
              v-if="siteControls.controls.enableMarket"
              class="nav-dropdown-container"
              @mouseenter="handleMarketMouseEnter"
              @mouseleave="handleMarketMouseLeave"
            >
              <button
                class="market-btn"
                @click.prevent="toggleMarketDropdown"
                :class="{ 'show': showMarketDropdown }"
              >
                <i class="fas fa-chart-line mr-1"></i>
                Market
                <i class="fas fa-chevron-down ml-1 chevron-icon"></i>
              </button>
              <div
                class="dropdown-menu"
                :class="{ 'show': showMarketDropdown }"
              >
                <router-link
                  v-if="siteControls.controls.enableLoan"
                  to="/market/loans"
                  class="dropdown-item"
                  @click="showMarketDropdown = false; mobileMenuOpen = false"
                >
                  <i class="fas fa-file-invoice-dollar mr-2"></i>Loans
                </router-link>
                <router-link
                  v-if="siteControls.controls.enableBonds"
                  to="/market/bonds"
                  class="dropdown-item"
                  @click="showMarketDropdown = false; mobileMenuOpen = false"
                >
                  <i class="fas fa-layer-group mr-2"></i>Bonds
                </router-link>
                <router-link
                  v-if="siteControls.controls.enableTrade"
                  to="/market/trades"
                  class="dropdown-item"
                  @click="showMarketDropdown = false; mobileMenuOpen = false"
                >
                  <i class="fas fa-exchange-alt mr-2"></i>Trades
                </router-link>
                <router-link
                  v-if="siteControls.controls.enableAuction"
                  to="/market/auctions"
                  class="dropdown-item"
                  @click="showMarketDropdown = false; mobileMenuOpen = false"
                >
                  <i class="fas fa-gavel mr-2"></i>Auctions
                </router-link>
                <router-link
                  v-if="siteControls.controls.enableSafe"
                  to="/market/fund-raising"
                  class="dropdown-item"
                  @click="showMarketDropdown = false; mobileMenuOpen = false"
                >
                  <i class="fas fa-rocket mr-2"></i>Fund Raisers
                </router-link>
              </div>
            </div>
            <UserMenu />
          </div>
        </div>
      </div>
    </nav>

    <!-- Main Content -->
    <main class="container py-4">
      <router-view />
    </main>

    <!-- Footer -->
    <footer class="footer mt-auto py-4">
      <div class="container">
        <div class="d-flex flex-column flex-md-row justify-content-between align-items-center">
          <div class="text-muted small mb-2 mb-md-0">
            &copy; {{ new Date().getFullYear() }} Bank of Cardano. Matrix Financial Instruments.
          </div>
          <div class="text-muted small">
            Powered by <span class="text-boc-gold">Cardano</span> Blockchain
          </div>
        </div>
      </div>
    </footer>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import UserMenu from '@/components/common/UserMenu.vue'
import { useAuthStore } from '@/stores/auth'
import { useSiteControlsStore } from '@/stores/siteControls'
import { useWalletStore } from '@/stores/wallet'

const authStore = useAuthStore()
const walletStore = useWalletStore()
const siteControls = useSiteControlsStore()

// Start auth initialization immediately (not in onMounted) so the router
// guard can await it before the first navigation resolves.
authStore.initialize()

// After the optimistic auth state is set (initPromise resolves immediately
// after loading persisted tokens), reconnect the CIP-30 wallet if one was
// previously connected. This fires before the background token refresh.
authStore.initPromise.then(() => {
  if (authStore.isAuthenticated) {
    walletStore.reconnect()
  }
})

const showSmartContractsDropdown = ref(false)
const showSimulationDropdown = ref(false)
const showMarketDropdown = ref(false)
const mobileMenuOpen = ref(false)
const navHoverEnabled = ref(typeof window !== 'undefined' ? window.innerWidth >= 992 : true)
let navMediaQuery: MediaQueryList | null = null
const showSmartContractsMenu = computed(
  () =>
    siteControls.controls.enableCredentials ||
    siteControls.controls.enableLoan ||
    siteControls.controls.enableBonds ||
    siteControls.controls.enableTrade ||
    siteControls.controls.enableAssets ||
    siteControls.controls.enableGold ||
    siteControls.controls.enableAuction ||
    siteControls.controls.enableSafe ||
    siteControls.controls.enableFracture ||
    siteControls.controls.enableCds ||
    siteControls.controls.enableTrustFund,
)
const hasSmartContractFinancialItems = computed(
  () =>
    siteControls.controls.enableLoan ||
    siteControls.controls.enableBonds ||
    siteControls.controls.enableTrade ||
    siteControls.controls.enableAuction ||
    siteControls.controls.enableSafe ||
    siteControls.controls.enableFracture ||
    siteControls.controls.enableCds ||
    siteControls.controls.enableTrustFund,
)
const showSmartContractsDivider = computed(
  () =>
    (siteControls.controls.enableCredentials || siteControls.controls.enableAssets) &&
    hasSmartContractFinancialItems.value,
)

function toggleSmartContractsDropdown() {
  showSimulationDropdown.value = false
  showMarketDropdown.value = false
  showSmartContractsDropdown.value = !showSmartContractsDropdown.value
}

function toggleSimulationDropdown() {
  showSmartContractsDropdown.value = false
  showMarketDropdown.value = false
  showSimulationDropdown.value = !showSimulationDropdown.value
}

function toggleMarketDropdown() {
  showSmartContractsDropdown.value = false
  showSimulationDropdown.value = false
  showMarketDropdown.value = !showMarketDropdown.value
}

function handleSmartContractsMouseEnter() {
  if (!navHoverEnabled.value) return
  showSimulationDropdown.value = false
  showMarketDropdown.value = false
  showSmartContractsDropdown.value = true
}

function handleSmartContractsMouseLeave() {
  if (!navHoverEnabled.value) return
  showSmartContractsDropdown.value = false
}

function handleSimulationMouseEnter() {
  if (!navHoverEnabled.value) return
  showSmartContractsDropdown.value = false
  showMarketDropdown.value = false
  showSimulationDropdown.value = true
}

function handleSimulationMouseLeave() {
  if (!navHoverEnabled.value) return
  showSimulationDropdown.value = false
}

function handleMarketMouseEnter() {
  if (!navHoverEnabled.value) return
  showSmartContractsDropdown.value = false
  showSimulationDropdown.value = false
  showMarketDropdown.value = true
}

function handleMarketMouseLeave() {
  if (!navHoverEnabled.value) return
  showMarketDropdown.value = false
}

function handleMediaQueryChange(event: MediaQueryListEvent) {
  navHoverEnabled.value = event.matches
}

onMounted(() => {
  if (typeof window === 'undefined') return
  navMediaQuery = window.matchMedia('(min-width: 992px)')
  navHoverEnabled.value = navMediaQuery.matches
  navMediaQuery.addEventListener('change', handleMediaQueryChange)
})

onBeforeUnmount(() => {
  navMediaQuery?.removeEventListener('change', handleMediaQueryChange)
})
</script>

<style scoped src="./assets/css/extracted/App.css"></style>
